"use client";

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ModerationProfile } from "shared/ModerationProfile";
import type { OrganisationSummary } from "shared/Organisation";
import type { Page } from "shared/Page";
import type { PolicyPresetId } from "shared/PolicyPresetIds";
import type { PrivatePatchProjectBody } from "shared/PrivateApiSchemas";
import type { Project } from "shared/Project";
import type { ProjectTeam } from "shared/ProjectAccess";
import { FIRST_PARTY_COOKIES } from "shared/SharedConstants";
import { getCookie } from "src/lib/AppSharedUtils";
import { setCookie } from "src/lib/hooks/ClientHooks";
import type { PortalShellData } from "src/lib/PortalShellData";
import { PortalContext, type PortalContextType } from "./PortalContext";

// The API client (with Zod and api-def) loads on the first request: every docs page renders
// this provider, and signed-out visitors never make one.
const loadApi = () => import("src/CalmLensApi");

interface PortalProviderProps {
  initialShellData?: PortalShellData;
  children: React.ReactNode;
  defaultOrganisations?: OrganisationSummary[];
  /** The organisation the server opened the portal with. */
  defaultSelectedOrgId?: string | null;
  defaultProjectsPage?: Page<Project>;
  /**
   * The selected project cookie as read by the server. Pass it from server layouts: client components
   * can't read request cookies during SSR, so reading the cookie here would differ between the server
   * render and hydration.
   */
  defaultSelectedProjectId?: string | null;
  /** The caller's team in `defaultSelectedProjectId`, loaded by the server so role-gated UI renders in SSR. */
  defaultProjectTeam?: ProjectTeam;
  /** The caller's moderation profiles, loaded by the server with `defaultSelectedProjectId`. */
  defaultModerationProfiles?: ModerationProfile[];
}

const EMPTY_PROJECTS_PAGE: Page<Project> = {
  items: [],
  total: 0,
  hasMore: false,
  pageIndex: 0,
  pageSize: 10,
  ordering: [],
};

export const PortalProvider = (props: PortalProviderProps) => {
  const {
    children,
    defaultOrganisations,
    defaultSelectedOrgId = null,
    defaultProjectsPage = EMPTY_PROJECTS_PAGE,
    defaultSelectedProjectId,
    defaultProjectTeam,
    defaultModerationProfiles,
  } = props;

  // An explicit server selection (including null) takes precedence over the browser cookie.
  const [selectedProjectId, updateSelectedProjectId] = useState<string | null>(() =>
    defaultSelectedProjectId !== undefined
      ? defaultSelectedProjectId
      : getCookie(FIRST_PARTY_COOKIES.SELECTED_PROJECT_ID.name) || null,
  );
  const [initialShellData, setInitialShellData] = useState(props.initialShellData);
  const setSelectedProjectId = useCallback((projectId: string | null) => {
    updateSelectedProjectId(projectId);
    setInitialShellData(undefined);
  }, []);
  const [organisations, setOrganisations] = useState<OrganisationSummary[]>(
    defaultOrganisations ?? [],
  );
  const selectedOrgId = defaultSelectedOrgId;
  const selectedOrganisation = organisations.find((org) => org.id === selectedOrgId) ?? null;
  const selectOrganisation = useCallback((orgId: string) => {
    setCookie(FIRST_PARTY_COOKIES.SELECTED_ORGANISATION_ID.name, orgId, {
      expiresMs: FIRST_PARTY_COOKIES.SELECTED_ORGANISATION_ID.expirationMs,
    });
    setCookie(FIRST_PARTY_COOKIES.SELECTED_PROJECT_ID.name, null);
  }, []);
  const reloadOrganisations = useCallback(async () => {
    const { data } = await (await loadApi()).getOrganisations.submit({});
    setOrganisations(data.organisations);
    return data.organisations;
  }, []);
  const [projects, setProjects] = useState<Project[]>(defaultProjectsPage.items);
  const [projectsHasMore, setProjectsHasMore] = useState(defaultProjectsPage.hasMore);
  const [projectsPageIndex, setProjectsPageIndex] = useState(defaultProjectsPage.pageIndex);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // Use useMemo to derive selectedProject from selectedProjectId and projects
  const selectedProject = useMemo(() => {
    if (!selectedProjectId || projects.length === 0) {
      return null;
    }
    return projects.find((project) => project.id === selectedProjectId) || null;
  }, [selectedProjectId, projects]);

  const [teamState, setTeamState] = useState<{ projectId: string; team: ProjectTeam } | undefined>(
    () =>
      defaultSelectedProjectId && defaultProjectTeam
        ? { projectId: defaultSelectedProjectId, team: defaultProjectTeam }
        : undefined,
  );
  const [teamErrorProjectId, setTeamErrorProjectId] = useState<string>();
  const projectTeamError = teamErrorProjectId === selectedProjectId;
  const projectTeam = teamState?.projectId === selectedProjectId ? teamState.team : undefined;
  const reloadProjectTeam = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const { data } = await (await loadApi()).getProjectTeam.submit({
        params: { projectId: selectedProjectId },
      });
      setTeamState({ projectId: selectedProjectId, team: data });
      setTeamErrorProjectId(undefined);
    } catch (error) {
      setTeamState(undefined);
      setTeamErrorProjectId(selectedProjectId);
      throw error;
    }
  }, [selectedProjectId]);
  // The project whose team is loaded or loading. A server-provided team counts as loaded, so mounting
  // (twice, in Strict Mode) doesn't fetch it again; focus and access changes still refresh it.
  const loadedTeamProjectId = useRef(
    defaultProjectTeam ? (defaultSelectedProjectId ?? undefined) : undefined,
  );
  useEffect(() => {
    const refresh = () => {
      void reloadProjectTeam().catch(() => setTeamState(undefined));
    };
    if (loadedTeamProjectId.current !== selectedProjectId) {
      loadedTeamProjectId.current = selectedProjectId ?? undefined;
      refresh();
    }
    window.addEventListener("focus", refresh);
    window.addEventListener("project-access-changed", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("project-access-changed", refresh);
    };
  }, [reloadProjectTeam, selectedProjectId]);

  // The organisation and project on screen (from the page's URL) become the default for a bare
  // `/portal`, through these cookies.
  useEffect(() => {
    if (!selectedOrgId) return;
    setCookie(FIRST_PARTY_COOKIES.SELECTED_ORGANISATION_ID.name, selectedOrgId, {
      expiresMs: FIRST_PARTY_COOKIES.SELECTED_ORGANISATION_ID.expirationMs,
    });
  }, [selectedOrgId]);

  // Update cookie when selectedProjectId changes
  useEffect(() => {
    if (selectedProjectId) {
      setCookie(FIRST_PARTY_COOKIES.SELECTED_PROJECT_ID.name, selectedProjectId, {
        expiresMs: FIRST_PARTY_COOKIES.SELECTED_PROJECT_ID.expirationMs,
      });
    } else {
      setCookie(FIRST_PARTY_COOKIES.SELECTED_PROJECT_ID.name, null);
    }
  }, [selectedProjectId]);

  // Auto-select first project if no project is selected and projects are available
  useEffect(() => {
    if (!selectedProjectId && projects.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
  }, [selectedProjectId, projects]);

  const createProject = async (
    name: string,
    description?: string,
    policyPresetId?: PolicyPresetId,
  ): Promise<Project> => {
    const response = await (await loadApi()).postProject.submit({
      body: {
        name,
        description,
        policyPresetId,
        ...(selectedOrgId ? { orgId: selectedOrgId } : {}),
      },
    });

    const newProject = response.data.project;
    setProjects((prev) => [...prev, newProject]);
    setSelectedProjectId(newProject.id);

    return newProject;
  };

  const loadMoreProjects = async () => {
    if (!projectsHasMore || projectsLoading) {
      return;
    }

    setProjectsLoading(true);
    try {
      const nextPageIndex = projectsPageIndex + 1;
      const page = (
        await (
          await loadApi()
        ).getProjectsPage.submit({
          query: {
            pageIndex: nextPageIndex,
            pageSize: defaultProjectsPage.pageSize,
            ...(selectedOrgId ? { orgId: selectedOrgId } : {}),
          },
        })
      ).data;
      setProjects((prev) => {
        const existingProjectIds = new Set(prev.map((project) => project.id));
        return [...prev, ...page.items.filter((project) => !existingProjectIds.has(project.id))];
      });
      setProjectsHasMore(page.hasMore);
      setProjectsPageIndex(page.pageIndex);
    } finally {
      setProjectsLoading(false);
    }
  };

  const updateProject = async (
    projectId: string,
    updates: PrivatePatchProjectBody,
  ): Promise<Project> => {
    const response = await (await loadApi()).patchProject.submit({
      params: { projectId },
      body: updates,
    });

    const updatedProject = response.data.project;
    setProjects((prev) => prev.map((p) => (p.id === projectId ? updatedProject : p)));

    return updatedProject;
  };

  const [moderationProfiles, setModerationProfiles] = useState<ModerationProfile[]>(
    defaultModerationProfiles ?? [],
  );
  const [moderationProfilesError, setModerationProfilesError] = useState(false);
  const reloadModerationProfiles = useCallback(async () => {
    if (!selectedOrgId) return [];
    try {
      const { data } = await (await loadApi()).getModerationProfiles.submit({
        params: { orgId: selectedOrgId },
      });
      setModerationProfiles(data.profiles);
      setModerationProfilesError(false);
      return data.profiles;
    } catch (error) {
      setModerationProfilesError(true);
      throw error;
    }
  }, [selectedOrgId]);
  // Policies belong to the organisation, which is fixed for this provider (switching organisation
  // remounts it), so they load once: server-provided profiles count as loaded, and switching
  // between the organisation's projects keeps them.
  const profilesLoaded = useRef(Boolean(defaultModerationProfiles));
  useEffect(() => {
    if (!selectedOrgId || profilesLoaded.current) return;
    profilesLoaded.current = true;
    void reloadModerationProfiles().catch(() => {});
  }, [selectedOrgId, reloadModerationProfiles]);

  const value: PortalContextType = {
    initialShellData,
    organisations,
    selectedOrganisation,
    selectedOrgId,
    selectOrganisation,
    reloadOrganisations,
    projectTeam,
    projectTeamError,
    reloadProjectTeam,
    moderationProfiles,
    moderationProfilesError,
    reloadModerationProfiles,
    selectedProject,
    selectedProjectId,
    projects,
    projectsHasMore,
    projectsPageIndex,
    projectsLoading,
    setSelectedProjectId,
    setProjects,
    loadMoreProjects,
    createProject,
    updateProject,
  };

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};
